<template>
  <div class="video-translation-view">
    <!-- Toast 提示组件 -->
    <div v-if="toast.show" class="toast-container" :class="toast.type" role="status" aria-live="polite">
      <div class="toast-content">
        <span class="toast-icon" aria-hidden="true">{{ toast.icon }}</span>
        <span class="toast-message">{{ toast.message }}</span>
      </div>
    </div>

    <!-- 分步视频翻译主界面 -->
    <div class="video-translation">
      <h2>精细视频翻译</h2>
      <div class="notice-text">
        注意：当前视频对口型仅能解决全程单人出镜且每一帧都有正脸的视频，无法解决多人、转头、面部遮挡或无脸画面。视频翻译的准确性受限字幕准确率，建议采用分步翻译人工校准字幕文件。
      </div>
      <div class="subtitle-desc">
        视频字幕生成、字幕翻译、语音翻译
      </div>

      <div class="stepwise-container">
        <div class="current-task-section">
          <!-- 当前任务卡片向导 -->
          <div class="wizard-card">
          <div class="wizard-card-header">
            <div class="wizard-title-line">
              <h3 class="wizard-title">当前任务：</h3>
              <span class="wizard-title-name">{{ currentTaskName }}</span>
            </div>
            <div class="wizard-header-actions">
              <button class="add-job-btn" @click="startNewTask">
                <span class="btn-icon" aria-hidden="true">+</span> 新建任务
              </button>
              <button class="refresh-status-btn" :disabled="isRefreshing" @click="refreshStepwiseStatus">
                {{ isRefreshing ? '刷新中...' : '刷新状态' }}
              </button>
            </div>
          </div>

          <div class="wizard-steps">
            <div
              v-for="step in wizardSteps"
              :key="step.value"
              class="wizard-step"
              :class="{
                active: wizardStep === step.value,
                completed: wizardStep > step.value,
                disabled: !canGoStep(step.value)
              }"
              @click="goToStep(step.value)"
            >
              <div class="step-number">{{ step.value }}</div>
              <div class="step-content">
                <div class="step-title">{{ step.title }}</div>
                <div class="step-desc">{{ step.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!currentRow" class="wizard-empty">正在初始化任务...</div>

        <div v-else class="wizard-body">
          <!-- 第 1 步：上传视频 + 目标语言 -->
          <section v-show="wizardStep === 1" class="wizard-panel">
            <h4>第 1 步：上传视频并生成字幕</h4>
            <div
              v-if="!currentRow.selectedFile && !currentRow.srtTaskId"
              class="wizard-upload-area"
              :class="{ dragging: currentRow.isDragging }"
              role="button"
              tabindex="0"
              aria-label="上传视频文件"
              @click="triggerInputRowFileUpload(0)"
              @keydown.enter.prevent="triggerInputRowFileUpload(0)"
              @keydown.space.prevent="triggerInputRowFileUpload(0)"
              @drop.prevent="handleInputRowFileDrop($event, 0)"
              @dragover.prevent="handleInputRowDragOver($event, 0)"
              @dragleave.prevent="handleInputRowDragLeave($event, 0)"
            >
              <span class="table-upload-icon" aria-hidden="true">+</span>
              <p>点击上传或拖拽视频文件</p>
              <small>支持视频格式，最长 60 分钟</small>
              <input
                type="file"
                :ref="'inputRowFileInput0'"
                accept="video/*"
                style="display:none"
                @click.stop
                @change="handleInputRowFileSelect($event, 0)"
              >
            </div>

            <div v-else class="wizard-upload-area wizard-upload-area--preview">
              <video
                v-if="currentRow.previewUrl"
                :src="currentRow.previewUrl"
                class="video-preview"
                controls
                preload="metadata"
              ></video>
              <div v-else class="preview-placeholder">视频已提交</div>
              <button
                v-if="!currentRow.srtTaskId"
                class="cell-delete-btn video-delete-btn"
                title="删除视频"
                @click.stop="clearInputRowFile(0)"
              >
                ×
              </button>
            </div>

            <div class="wizard-form-row">
              <span>目标语言：</span>
              <select v-model="currentRow.targetLanguage" class="lang-select" :disabled="targetLanguageLocked">
                <option v-for="item in languages" :key="item.value" :value="item.value">{{ item.label }}</option>
              </select>
              <button
                v-if="!currentRow.srtTaskId"
                class="generate-subtitle-btn"
                :disabled="!currentRow.selectedFile || !currentRow.videoDuration || currentRow.srtStatus === 'processing'"
                @click="generateSubtitleForInputRow(0)"
              >
                开始生成字幕
              </button>
              <button v-else class="start-translate-btn" @click="goToStep(2)">进入第 2 步</button>
            </div>
          </section>

          <!-- 第 2 步：等待字幕生成并下载 -->
          <section v-show="wizardStep === 2" class="wizard-panel wizard-panel--center">
            <h4>第 2 步：等待字幕生成并下载</h4>
            <div class="wizard-current-file">当前处理任务：{{ currentTaskName }}</div>
            <div v-if="!currentRow.srtTaskId" class="wizard-lock">
              请先完成第 1 步。
              <button class="link-btn" @click="goToStep(1)">返回第 1 步</button>
            </div>
            <template v-else>
              <div class="status-line">
                <span v-if="currentRow.srtStatus === 'processing'" class="status-processing">字幕生成中，请点击右上角“刷新状态”查看进度</span>
                <span v-else-if="currentRow.srtStatus === 'failed'" class="status-failed">字幕生成失败</span>
                <span v-else-if="currentRow.srtStatus === 'completed'" class="status-success">字幕已生成</span>
                <span v-else class="status-idle">等待处理</span>
              </div>
              <div class="wizard-actions">
                <button v-if="currentRow.srtStatus === 'completed'" class="download-btn" @click="downloadInputRowSubtitle(0)">下载 SRT 字幕</button>
                <button v-if="currentRow.srtStatus === 'completed'" class="start-translate-btn" @click="goToStep(3)">进入第 3 步</button>
                <button v-if="currentRow.srtStatus === 'failed'" class="reset-job-btn" @click="startNewTask">重新创建任务</button>
              </div>
            </template>
          </section>

          <!-- 第 3 步：上传修正后的 SRT -->
          <section v-show="wizardStep === 3" class="wizard-panel wizard-panel--center">
            <h4>第 3 步：上传修正后的 SRT</h4>
            <div v-if="currentRow.srtStatus !== 'completed'" class="wizard-lock">
              第 1 步字幕尚未完成，暂不能上传修正字幕。
              <button class="link-btn" @click="goToStep(2)">返回第 2 步</button>
            </div>
            <template v-else>
              <input
                type="file"
                :ref="'inputRowSubtitleInput0'"
                accept=".srt"
                style="display:none"
                @click.stop
                @change="handleInputRowSubtitleUpload($event, 0)"
              >
              <div v-if="currentRow.uploadedSubtitleName" class="wizard-file-info">
                <div>
                  <div class="file-label">修正后的字幕</div>
                  <div class="file-name">{{ currentRow.uploadedSubtitleName }}</div>
                  <div v-if="currentRow.uploadedSubtitleSize" class="file-meta">
                    大小：{{ formatFileSize(currentRow.uploadedSubtitleSize) }}
                  </div>
                </div>
                <span class="uploaded-check" aria-hidden="true">✓</span>
              </div>
              <div class="wizard-actions">
                <button v-if="!currentRow.uploadedSubtitleName" class="upload-btn" @click="triggerInputRowSubtitleUpload(0)">上传修正后的 SRT</button>
                <button v-if="currentRow.uploadedSubtitleName" class="start-translate-btn" @click="goToStep(4)">进入第 4 步</button>
                <button v-if="currentRow.uploadedSubtitleName" class="upload-btn" @click="triggerInputRowSubtitleUpload(0)">重新上传</button>
              </div>
            </template>
          </section>

          <!-- 第 4 步：选择硬字幕 / 对口型并开始翻译 -->
          <section v-show="wizardStep === 4" class="wizard-panel wizard-panel--center">
            <h4>第 4 步：选择输出并开始翻译</h4>
            <div v-if="!currentRow.uploadedSubtitleUrl" class="wizard-lock">
              请先上传修正后的 SRT 字幕。
              <button class="link-btn" @click="goToStep(3)">返回第 3 步</button>
            </div>
            <template v-else>
              <div class="option-row option-row--center">
                <label class="option-checkbox">
                  <input v-model="currentRow.hardSubtitle" type="checkbox" :disabled="translationLocked">
                  <span>是否写入硬字幕</span>
                </label>
                <label class="option-checkbox">
                  <input v-model="currentRow.lipSync" type="checkbox" :disabled="translationLocked">
                  <span>是否对口型</span>
                </label>
              </div>
              <div v-if="currentRow.hardSubtitle" class="subtitle-type-row">
                <span>字幕类型：</span>
                <label class="option-checkbox">
                  <input v-model="currentRow.subtitleOption" type="radio" value="source" :disabled="translationLocked">
                  <span>原语言字幕</span>
                </label>
                <label class="option-checkbox">
                  <input v-model="currentRow.subtitleOption" type="radio" value="target" :disabled="translationLocked">
                  <span>目标语言字幕</span>
                </label>
                <label class="option-checkbox">
                  <input v-model="currentRow.subtitleOption" type="radio" value="bilingual" :disabled="translationLocked">
                  <span>双语字幕</span>
                </label>
              </div>
              <div class="cost-line">
                预计费用：<strong>¥{{ translationCost.toFixed(2) }}</strong>
                <span class="cost-detail">（{{ translationRate }} 元/分钟）</span>
              </div>
              <div v-if="!translationSubmitted" class="wizard-actions">
                <button class="start-translate-btn" @click="startInputRowTranslation(0)">开始翻译</button>
              </div>
              <div v-else class="wizard-lock wizard-lock--center">
                翻译任务已提交，请在下方历史任务中查看处理进度。
              </div>
            </template>
          </section>
          </div>
        </div>

        <!-- 历史任务列表：只读 -->
        <div class="history-section">
          <div class="job-table-header">
            <h3 class="job-table-title">历史任务</h3>
            <button class="refresh-status-btn" :disabled="isRefreshing" @click="refreshStepwiseStatus">
              {{ isRefreshing ? '刷新中...' : '刷新状态' }}
            </button>
          </div>
          <div v-if="displayedJobs.length === 0" class="empty-history-msg">暂无历史任务</div>
          <div v-else class="history-list">
            <div v-for="job in displayedJobs" :key="job.id" class="history-item">
              <div class="history-main">
                <div class="history-name" :title="job.filename">{{ job.filename }}</div>
                <div class="history-meta">
                  创建时间：{{ job.createdAt || '-' }} · 目标语言：{{ languageLabel(job.targetLanguage) }}
                </div>
                <div class="history-status" :class="historyStatusClass(job)">{{ historyStatusText(job) }}</div>
              </div>
              <div class="history-actions">
                <button v-if="job.subtitleStatus === 'completed'" class="download-btn" @click="downloadSubtitle(job)">下载 SRT</button>
                <button v-if="job.translationStatus === 'completed'" class="download-btn" @click="downloadTranslatedVideo(job)">下载视频</button>
                <button v-if="canContinueJob(job)" class="start-translate-btn" @click="continueJob(job)">继续任务</button>
                <button v-if="isFailedJob(job)" class="delete-job-btn" @click="deleteStepwiseJob(job)">删除记录</button>
                <span v-else-if="job.translationStatus === 'completed'" class="status-success">已完成</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="terms">
        仅对本人视频翻译，您承诺您的素材内容为您本人所有或已获得合法授权。并遵守《音视频翻译功能使用规范》
      </p>
    </div>

    <!-- 删除确认对话框 -->
    <div v-if="showDeleteConfirm" class="confirm-dialog-overlay" @click.self="showDeleteConfirm = false">
      <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-title">
        <h3 id="delete-title">确认删除</h3>
        <p>{{ deleteConfirmText }}</p>
        <div class="confirm-dialog-buttons">
          <button class="cancel-btn" @click="showDeleteConfirm = false">取消</button>
          <button class="confirm-btn" @click="handleDeleteConfirm">删除</button>
        </div>
      </div>
    </div>

    <!-- 确认收费对话框 -->
    <div v-if="showConfirmPricing" class="confirm-dialog-overlay" @click.self="showConfirmPricing = false">
      <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="pricing-title">
        <h3 id="pricing-title">确认费用</h3>
        <p>{{ confirmMessage }}</p>
        <div class="confirm-dialog-buttons">
          <button class="cancel-btn" @click="showConfirmPricing = false">取消</button>
          <button class="confirm-btn confirm-btn--primary" @click="handleConfirmPricing">确定</button>
        </div>
      </div>
    </div>

    <!-- 处理中提示 -->
    <div v-if="isProcessing" class="processing-overlay">
      <div class="processing-dialog" role="alertdialog" aria-modal="true" aria-live="assertive">
        <div class="spinner" aria-hidden="true"></div>
        <h3>{{ processingMessage }}</h3>

        <!-- 上传进度：大文件上传耗时较长，这里给出真实进度，避免被误认为卡死 -->
        <div v-if="isUploading" class="upload-progress">
          <div class="upload-progress-track">
            <div class="upload-progress-bar" :style="{ width: uploadPercent + '%' }"></div>
          </div>
          <p class="upload-progress-text">
            {{ uploadPercent.toFixed(1) }}% · {{ uploadLoadedText }} · {{ uploadSpeedText }}
          </p>
          <p v-if="uploadSlowTip" class="upload-slow-tip">{{ uploadSlowTip }}</p>
          <button class="cancel-upload-btn" @click="cancelUpload">取消上传</button>
        </div>
        <p v-else>请勿关闭页面...</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUpQiniuToken,
  uploadToQiniu,
  getUserRecords,
  deleteUserRecord,
  saveStepwiseVideoSrtFileUrl,
  validateStepwiseVideoSrtTask,
  saveStepwiseVideoTranslationFileUrl
} from '@/api/upfile'
import { QINIU_BASE_URL, stepwiseVideoTranslateLanguages } from '@/api/constants'
import { mapGetters } from 'vuex'
import uploadProgressMixin from '@/utils/uploadProgressMixin'

const MAX_VIDEO_DURATION = 60 * 60 // 视频最长 60 分钟（秒）
const MAX_VIDEO_SIZE = 2000 * 1024 * 1024 // 视频最大 2000MB
const MAX_SRT_SIZE = 10 * 1024 * 1024 // 字幕最大 10MB

export default {
  name: 'StepwiseVideoTranslation',
  mixins: [uploadProgressMixin],
  data () {
    return {
      // 语言与字幕选项
      languages: stepwiseVideoTranslateLanguages,

      // 分步翻译 — 多任务输入行支持（数组）
      inputRows: [],
      activeRowId: null,
      nextRowId: 1,
      wizardStep: 1,
      wizardSteps: [
        { value: 1, title: '上传视频', desc: '选择视频和目标语言' },
        { value: 2, title: '生成字体', desc: '等待并下载 SRT' },
        { value: 3, title: '修正字幕', desc: '上传修改后的 SRT' },
        { value: 4, title: '合成视频', desc: '选择输出并开始翻译' }
      ],

      // 记录相关
      stepwiseRecords: [],
      translationJobs: [],
      isRefreshing: false,

      // 删除相关
      showDeleteConfirm: false,
      deleteMode: null,
      pendingRowIndex: null,
      pendingDeleteJob: null,

      // 收费确认相关
      showConfirmPricing: false,
      confirmMessage: '',
      pendingAction: null,

      // 处理中提示
      isProcessing: false,
      processingMessage: '',

      toast: {
        show: false,
        message: '',
        type: 'success',
        icon: '✓'
      },
      // 提示计时器（仅内部使用）
      toastTimer: null,

      // 价格配置（单位：元/分钟）
      pricing: {
        subtitle: 1, // 分步-生成字幕
        synthesis: 1, // 分步-语音合成
        lipSyncSynthesis: 4 // 分步-语音合成 + 对口型
      }
    }
  },
  computed: {
    ...mapGetters(['balance', 'isLoggedIn']),
    currentRow () {
      return this.inputRows[0] || null
    },
    currentTaskName () {
      const row = this.currentRow
      if (!row) return '正在初始化...'
      return row.filename || this.fileDisplayName(row) || '新建分步视频翻译'
    },
    targetLanguageLocked () {
      const row = this.currentRow
      if (!row) return true
      return !!row.srtTaskId ||
        row.srtStatus === 'processing' ||
        row.translationStatus === 'translating' ||
        row.translationStatus === 'completed'
    },
    translationLocked () {
      const row = this.currentRow
      if (!row) return true
      return row.translationStatus === 'translating' || row.translationStatus === 'completed'
    },
    translationRate () {
      const row = this.currentRow
      return row && row.lipSync ? this.pricing.lipSyncSynthesis : this.pricing.synthesis
    },
    translationCost () {
      const row = this.currentRow
      if (!row || !row.videoDuration) return 0
      return (row.videoDuration / 60) * this.translationRate
    },
    translationSubmitted () {
      const row = this.currentRow
      if (!row) return false
      return row.translationStatus === 'translating' ||
        row.translationStatus === 'completed' ||
        row.translationStatus === 'failed'
    },
    // 历史任务列表（包含当前任务，便于提交后直接在历史中查看状态）
    displayedJobs () {
      return this.translationJobs
    },
    deleteConfirmText () {
      if (this.deleteMode === 'stepwiseJob') {
        return '删除后该任务的字幕与翻译记录都会被移除，此操作无法撤销。'
      }
      if (this.deleteMode === 'inputRow') {
        return '该任务已提交处理，删除后记录仍会保留在历史任务中，确定要删除吗？'
      }
      return '确定要执行删除操作吗？此操作无法撤销。'
    }
  },
  mounted () {
    if (this.isLoggedIn) {
      this.refreshStepwiseStatus(true)
    }
    if (this.inputRows.length === 0) {
      this.addNewJobRow(false)
    }
  },
  beforeDestroy () {
    this.inputRows.forEach(row => {
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
    })
    if (this.toastTimer) clearTimeout(this.toastTimer)
  },
  methods: {
    // ─── Toast ────────────────────────────────────────────
    showToast (message, type = 'success') {
      const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' }
      this.toast.message = message
      this.toast.type = type
      this.toast.icon = icons[type] || icons.info
      this.toast.show = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toast.show = false }, 3000)
    },
    showError (message) { this.showToast(message, 'error') },
    showSuccess (message) { this.showToast(message, 'success') },

    // ─── 工具 ─────────────────────────────────────────────
    // 任务行在生成字幕/翻译过程中锁定设置
    inputRowLocked (row) {
      return !!row.srtTaskId ||
        row.srtStatus === 'processing' ||
        row.translationStatus === 'translating' ||
        row.translationStatus === 'completed'
    },
    formatTaskStatus (status) {
      const statusMap = {
        pending: '等待处理',
        processing: '正在处理',
        completed: '处理完成',
        failed: '处理失败'
      }
      return statusMap[status] || status || '-'
    },
    generateQiniuFileName (prefix, originalFilename = 'video.mp4') {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
      const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
      const ms = String(now.getMilliseconds()).padStart(3, '0') + Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      let uuidShort
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        uuidShort = crypto.randomUUID().replace(/-/g, '').slice(0, 4)
      } else {
        uuidShort = Math.random().toString(16).substring(2, 6)
      }
      const ext = (originalFilename || '').split('.').pop() || 'bin'
      return `${prefix}_${datePart}_${timePart}_${ms}_${uuidShort}.${ext}`
    },
    triggerDownload (url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    fileDisplayName (row) {
      if (!row) return ''
      return row.filename || (row.selectedFile && row.selectedFile.name) || ''
    },
    formatDuration (seconds) {
      const total = Math.max(0, Math.floor(Number(seconds) || 0))
      const minutes = Math.floor(total / 60)
      const remain = total % 60
      return `${minutes} 分 ${String(remain).padStart(2, '0')} 秒`
    },
    formatFileSize (bytes) {
      const size = Number(bytes) || 0
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(2)} MB`
    },
    languageLabel (value) {
      const item = this.languages.find(lang => lang.value === value)
      return item ? item.label : (value || '-')
    },
    canGoStep (step) {
      const row = this.currentRow
      if (step === 1) return true
      if (!row) return false
      if (step === 2) return !!row.srtTaskId
      if (step === 3) return row.srtStatus === 'completed' && !!row.srtTaskId
      if (step === 4) return row.srtStatus === 'completed' && !!row.uploadedSubtitleUrl
      return false
    },
    goToStep (step) {
      if (!this.canGoStep(step)) {
        const reasons = {
          2: '请先提交第 1 步生成字幕',
          3: '请先等待第 1 步字幕生成完成',
          4: '请先上传修正后的 SRT 字幕'
        }
        this.showError(reasons[step] || '当前步骤暂不可用')
        return
      }
      this.wizardStep = step
    },
    canContinueJob (job) {
      return !!job.srtTaskId &&
        job.translationStatus !== 'completed' &&
        job.translationStatus !== 'failed'
    },
    historyStatusText (job) {
      if (job.subtitleStatus === 'processing' || job.subtitleStatus === 'pending') return '字幕生成中'
      if (job.subtitleStatus === 'failed') return '字幕生成失败'
      if (job.translationStatus === 'translating') return '翻译合成中'
      if (job.translationStatus === 'failed') return '翻译合成失败'
      if (job.translationStatus === 'completed') return '已完成'
      if (job.subtitleStatus === 'completed' && !job.uploadedSubtitleUrl) return '待上传修正字幕'
      if (job.uploadedSubtitleUrl) return '待开始翻译'
      return '待处理'
    },
    historyStatusClass (job) {
      if (job.subtitleStatus === 'failed' || job.translationStatus === 'failed') return 'status-failed'
      if (job.translationStatus === 'completed') return 'status-success'
      if (job.subtitleStatus === 'processing' || job.translationStatus === 'translating') return 'status-processing'
      return 'status-idle'
    },
    isFailedJob (job) {
      return job.subtitleStatus === 'failed' || job.translationStatus === 'failed'
    },

    startNewTask () {
      this.inputRows.forEach(row => {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      })
      this.inputRows.splice(0, this.inputRows.length)
      this.addNewJobRow()
    },
    continueJob (job) {
      if (!this.canContinueJob(job)) return
      const row = {
        rowId: this.nextRowId++,
        selectedFile: null,
        filename: job.filename || '',
        previewUrl: '',
        videoFileUrl: job.videoFileUrl || '',
        videoDuration: job.duration || 0,
        isDragging: false,
        srtStatus: job.subtitleStatus || 'idle',
        srtUrl: job.subtitleUrl || '',
        srtTaskId: job.srtTaskId || '',
        translateTaskId: job.translateTaskId || '',
        uploadedSubtitleUrl: job.uploadedSubtitleUrl || '',
        uploadedSubtitleName: job.uploadedSubtitleName || '',
        uploadedSubtitleSize: 0,
        targetLanguage: job.targetLanguage || 'en',
        hardSubtitle: !!job.hardSubtitle,
        subtitleOption: job.subtitleOption || 'target',
        hardSubtitleType: job.hardSubtitleType || 'target',
        lipSync: !!job.lipSync,
        translationStatus: job.translationStatus || 'idle',
        translatedUrl: job.translatedUrl || ''
      }
      this.inputRows.splice(0, this.inputRows.length, row)
      this.activeRowId = row.rowId
      if (row.srtStatus === 'completed' && row.uploadedSubtitleUrl) {
        this.wizardStep = 4
      } else if (row.srtStatus === 'completed') {
        this.wizardStep = 3
      } else if (row.srtTaskId) {
        this.wizardStep = 2
      } else {
        this.wizardStep = 1
      }
      this.showSuccess('已载入历史任务，可继续处理')
    },

    // ─── 任务行 ───────────────────────────────────────────
    addNewJobRow (showToast = true) {
      const newRow = {
        rowId: this.nextRowId++,
        selectedFile: null,
        filename: '',
        previewUrl: '',
        videoFileUrl: '',
        videoDuration: 0,
        isDragging: false,
        srtStatus: 'idle',
        srtUrl: '',
        srtTaskId: '',
        translateTaskId: '',
        uploadedSubtitleUrl: '',
        uploadedSubtitleName: '',
        uploadedSubtitleSize: 0,
        targetLanguage: 'en',
        hardSubtitle: false,
        subtitleOption: 'target',
        hardSubtitleType: 'target',
        lipSync: false,
        translationStatus: 'idle',
        translatedUrl: ''
      }
      this.inputRows.push(newRow)
      this.activeRowId = newRow.rowId
      this.wizardStep = 1
      if (showToast) this.showSuccess('已添加新任务行')
    },
    deleteInputRow (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (row && (row.srtTaskId || row.translateTaskId)) {
        this.deleteMode = 'inputRow'
        this.pendingRowIndex = rowIndex
        this.showDeleteConfirm = true
        return
      }
      this.inputRows.splice(rowIndex, 1)
      if (this.activeRowId === (row && row.rowId)) {
        this.activeRowId = this.inputRows.length > 0 ? this.inputRows[0].rowId : null
      }
      this.showSuccess('任务行已删除')
    },
    resetInputRow (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      const hasSubmitted = !!(row.srtTaskId || row.translateTaskId)
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      Object.assign(row, {
        selectedFile: null,
        filename: '',
        previewUrl: '',
        videoFileUrl: '',
        videoDuration: 0,
        isDragging: false,
        srtStatus: 'idle',
        srtUrl: '',
        srtTaskId: '',
        translateTaskId: '',
        uploadedSubtitleUrl: '',
        uploadedSubtitleName: '',
        uploadedSubtitleSize: 0,
        targetLanguage: 'en',
        hardSubtitle: false,
        subtitleOption: 'target',
        hardSubtitleType: 'target',
        lipSync: false,
        translationStatus: 'idle',
        translatedUrl: ''
      })
      this.resetRowFileInputs(rowIndex)
      this.wizardStep = 1
      this.showSuccess(hasSubmitted ? '任务行已重置，已提交的任务可在下方历史记录中查看' : '任务行已重置')
    },
    resetRowFileInputs (rowIndex) {
      const fileRef = this.getDomRef('inputRowFileInput' + rowIndex)
      const subtitleRef = this.getDomRef('inputRowSubtitleInput' + rowIndex)
      if (fileRef) fileRef.value = ''
      if (subtitleRef) subtitleRef.value = ''
    },
    getDomRef (name) {
      const ref = this.$refs[name]
      return Array.isArray(ref) ? ref[0] : ref
    },

    // ─── 任务行 — 视频上传 ────────────────────────────────
    triggerInputRowFileUpload (rowIndex) {
      const input = this.getDomRef('inputRowFileInput' + rowIndex)
      if (input) input.click()
    },
    handleInputRowFileSelect (event, rowIndex) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processInputRowFile(file, rowIndex)
      } else if (file) {
        this.showError('请选择有效的视频文件')
      }
    },
    handleInputRowFileDrop (event, rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      row.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        this.processInputRowFile(file, rowIndex)
      } else if (file) {
        this.showError('请拖拽有效的视频文件')
      }
    },
    handleInputRowDragOver (event, rowIndex) {
      const row = this.inputRows[rowIndex]
      if (row) row.isDragging = true
    },
    handleInputRowDragLeave (event, rowIndex) {
      const row = this.inputRows[rowIndex]
      if (row) row.isDragging = false
    },
    processInputRowFile (file, rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      if (file.size > MAX_VIDEO_SIZE) {
        this.showError('文件大小超过限制（最大 2000MB）')
        return
      }
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      row.selectedFile = file
      row.filename = file.name
      const url = URL.createObjectURL(file)
      row.previewUrl = url
      const tempVideo = document.createElement('video')
      tempVideo.preload = 'metadata'
      tempVideo.onloadedmetadata = () => {
        row.videoDuration = tempVideo.duration || 0
        if (tempVideo.duration > MAX_VIDEO_DURATION) {
          this.showError('视频时长超过限制（最大 60 分钟）')
          row.selectedFile = null
          row.filename = ''
          row.previewUrl = ''
          URL.revokeObjectURL(url)
          row.videoDuration = 0
          return
        }
        this.activeRowId = row.rowId
      }
      tempVideo.onerror = () => {
        URL.revokeObjectURL(url)
        this.showError('视频文件读取失败，请更换文件')
        row.selectedFile = null
        row.filename = ''
        row.previewUrl = ''
        row.videoDuration = 0
      }
      tempVideo.src = url
    },
    clearInputRowFile (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      row.selectedFile = null
      row.filename = ''
      row.previewUrl = ''
      row.videoDuration = 0
      this.resetRowFileInputs(rowIndex)
    },

    // ─── 任务行 — 生成字幕 ────────────────────────────────
    generateSubtitleForInputRow (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      this.activeRowId = row.rowId
      if (!this.isLoggedIn) { this.showError('请先登录'); return }
      if (!row.selectedFile) { this.showError('请先选择一个视频文件'); return }
      if (!row.videoDuration || row.videoDuration <= 0) { this.showError('视频信息读取中，请稍后再试'); return }
      if (row.videoDuration > MAX_VIDEO_DURATION) { this.showError('视频时长不能超过 60 分钟'); return }

      const cost = (row.videoDuration / 60) * this.pricing.subtitle
      if (this.balance < cost) {
        this.showError(`余额不足，当前余额 ¥${this.balance}，所需 ¥${cost.toFixed(2)}`)
        return
      }
      this.pendingAction = { type: 'subtitle', rowIndex }
      this.confirmMessage = `视频时长 ${(row.videoDuration / 60).toFixed(2)} 分钟，按 ${this.pricing.subtitle} 元/分钟计费，` +
        `本次生成字幕费用 ${cost.toFixed(2)} 元。确认开始吗？`
      this.showConfirmPricing = true
    },
    async confirmGenerateSubtitle (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      this.isProcessing = true
      this.processingMessage = '正在上传视频...'
      this.beginUploadProgress((row.selectedFile && row.selectedFile.size) || 0)
      try {
        const uploadFileName = this.generateQiniuFileName('stepwiseVideoSrt', row.selectedFile.name)
        // 传入 key：后端会把上传凭证限定到该文件，并使用更长的有效期
        const tokenResponse = await getUpQiniuToken(uploadFileName)
        if (tokenResponse.status === 401) {
          this.showError('身份验证失败，请重新登录')
          return
        }
        const qiniuUploadToken = tokenResponse.uploadToken
        const response = await uploadToQiniu(row.selectedFile, uploadFileName, qiniuUploadToken,
          this.createUploadHandlers())
        this.isUploading = false
        this.processingMessage = '视频上传完成，正在提交任务...'
        const videoFileUrl = `${QINIU_BASE_URL}/${response.data.key}`

        const result = await saveStepwiseVideoSrtFileUrl({
          operation_type: 'generateSubtitle',
          input_file_url: videoFileUrl,
          input_filename: row.selectedFile.name,
          target_lang: row.targetLanguage,
          video_duration: row.videoDuration
        })
        if (result && result.status === 200 && result.task_id) {
          if (typeof result.balance !== 'undefined') {
            this.$store.commit('setBalance', result.balance)
          }
          row.filename = row.selectedFile.name
          row.videoFileUrl = videoFileUrl
          row.srtTaskId = result.task_id
          row.srtStatus = 'processing'
          this.wizardStep = 2
          this.showSuccess('任务已提交，请点击“刷新状态”查看字幕生成进度')
          this.refreshStepwiseStatus(true)
        } else {
          this.showError((result && result.message) || '提交失败')
        }
      } catch (error) {
        if (this.isUploadCanceledError(error)) {
          this.showToast('已取消上传', 'info')
        } else {
          this.showError((error && error.message) || '上传失败，请重试')
        }
      } finally {
        this.endUploadProgress()
        this.isProcessing = false
      }
    },

    // ─── 任务行 — 上传修改后的字幕 ────────────────────────
    triggerInputRowSubtitleUpload (rowIndex) {
      const input = this.getDomRef('inputRowSubtitleInput' + rowIndex)
      if (input) input.click()
    },
    async handleInputRowSubtitleUpload (event, rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      const file = event.target.files[0]
      if (!file) return
      if (!/\.srt$/i.test(file.name)) {
        this.showError('请选择有效的 SRT 字幕文件')
        return
      }
      if (file.size > MAX_SRT_SIZE) {
        this.showError('字幕文件过大（最大 10MB）')
        return
      }
      if (!row.srtTaskId) {
        this.showError('第一步任务不存在，请先刷新状态')
        return
      }
      this.isProcessing = true
      this.processingMessage = '正在校验任务状态...'
      try {
        const validation = await validateStepwiseVideoSrtTask({ task_id: row.srtTaskId })
        if (!validation || validation.status !== 200) {
          this.showError((validation && validation.message) || '第一步字幕任务尚未完成')
          return
        }
        this.processingMessage = '上传字幕中...'
        const uploadFileName = this.generateQiniuFileName('stepwiseVideoSrtEdited', file.name)
        const tokenResponse = await getUpQiniuToken(uploadFileName)
        this.beginUploadProgress(file.size)
        const resp = await uploadToQiniu(file, uploadFileName, tokenResponse.uploadToken,
          this.createUploadHandlers())
        this.isUploading = false
        row.uploadedSubtitleUrl = `${QINIU_BASE_URL}/${resp.data.key}`
        row.uploadedSubtitleName = file.name
        row.uploadedSubtitleSize = file.size
        row.translationStatus = 'ready'
        this.wizardStep = 4
        this.showSuccess('字幕已上传，可以进入第 4 步开始翻译')
      } catch (err) {
        if (this.isUploadCanceledError(err)) {
          this.showToast('已取消上传', 'info')
        } else {
          this.showError((err && err.message) || '字幕上传失败')
        }
      } finally {
        this.endUploadProgress()
        this.isProcessing = false
      }
    },

    // ─── 任务行 — 开始翻译 ────────────────────────────────
    startInputRowTranslation (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row) return
      this.activeRowId = row.rowId
      this.requestTranslation({
        type: 'row',
        rowIndex,
        duration: row.videoDuration,
        lipSync: row.lipSync,
        hasTaskId: !!row.srtTaskId,
        hasSubtitle: !!row.uploadedSubtitleUrl
      })
    },
    async confirmStartTranslation (action) {
      const isRow = action.type === 'row'
      const target = isRow ? this.inputRows[action.rowIndex] : action.job
      if (!target) return
      this.isProcessing = true
      this.processingMessage = '正在提交翻译任务...'
      try {
        const result = await saveStepwiseVideoTranslationFileUrl({
          operation_type: 'translateWithEditedSubtitle',
          task_id: target.srtTaskId,
          subtitle_file_url: target.uploadedSubtitleUrl,
          hardSubtitle: !!target.hardSubtitle,
          subtitleOption: target.subtitleOption || 'target',
          lipSync: !!target.lipSync
        })
        if (result && result.status === 200 && result.task_id) {
          if (typeof result.balance !== 'undefined') {
            this.$store.commit('setBalance', result.balance)
          }
          target.translateTaskId = result.task_id
          target.translationStatus = 'translating'
          this.showSuccess('翻译任务已提交，请点击“刷新状态”查看处理进度')
          this.refreshStepwiseStatus(true)
        } else {
          this.showError((result && result.message) || '提交失败')
        }
      } catch (err) {
        this.showError('提交翻译失败，请重试')
      } finally {
        this.isProcessing = false
      }
    },
    // 费用校验 + 收费确认（任务行与历史任务共用）
    requestTranslation (payload) {
      if (!this.isLoggedIn) { this.showError('请先登录'); return }
      const rate = payload.lipSync ? this.pricing.lipSyncSynthesis : this.pricing.synthesis
      const cost = (payload.duration / 60) * rate

      if (!payload.hasTaskId) { this.showError('任务 ID 不存在，请先刷新状态'); return }
      if (!payload.hasSubtitle) { this.showError('请先上传修改后的字幕文件'); return }
      if (payload.duration > MAX_VIDEO_DURATION) { this.showError('视频时长不能超过 60 分钟'); return }
      if (this.balance < cost) {
        this.showError(`余额不足，当前余额 ¥${this.balance}，所需 ¥${cost.toFixed(2)}`)
        return
      }
      this.pendingAction = payload.type === 'row'
        ? { type: 'translation', rowIndex: payload.rowIndex }
        : { type: 'translation', job: payload.job }
      this.confirmMessage = `视频时长 ${(payload.duration / 60).toFixed(2)} 分钟，按 ${rate} 元/分钟计费，` +
        `本次语音克隆翻译费用 ${cost.toFixed(2)} 元。确认开始吗？`
      this.showConfirmPricing = true
    },
    startJobTranslation (job) {
      this.requestTranslation({
        type: 'job',
        job,
        duration: job.duration,
        lipSync: job.lipSync,
        hasTaskId: !!job.srtTaskId,
        hasSubtitle: !!job.uploadedSubtitleUrl
      })
    },

    // ─── 历史任务行 — 上传字幕 ────────────────────────────
    triggerJobSubtitleUpload (index) {
      const input = this.getDomRef('subtitleUpload' + index)
      if (input) input.click()
    },
    async handleJobSubtitleUpload (event, job) {
      const file = event.target.files[0]
      if (!file) return
      if (!/\.srt$/i.test(file.name)) {
        this.showError('请选择有效的 SRT 字幕文件')
        return
      }
      if (file.size > MAX_SRT_SIZE) {
        this.showError('字幕文件过大（最大 10MB）')
        return
      }
      if (!job.srtTaskId) {
        this.showError('第一步任务不存在，请先刷新状态')
        return
      }
      this.isProcessing = true
      this.processingMessage = '正在校验任务状态...'
      try {
        const validation = await validateStepwiseVideoSrtTask({ task_id: job.srtTaskId })
        if (!validation || validation.status !== 200) {
          this.showError((validation && validation.message) || '第一步字幕任务尚未完成')
          return
        }
        this.processingMessage = '上传字幕中...'
        const uploadFileName = this.generateQiniuFileName('stepwiseVideoSrtEdited', file.name)
        const tokenResponse = await getUpQiniuToken(uploadFileName)
        this.beginUploadProgress(file.size)
        const resp = await uploadToQiniu(file, uploadFileName, tokenResponse.uploadToken,
          this.createUploadHandlers())
        this.isUploading = false
        job.uploadedSubtitleUrl = `${QINIU_BASE_URL}/${resp.data.key}`
        job.uploadedSubtitleName = file.name
        job.translationStatus = 'ready'
        this.showSuccess('字幕已上传，请点击“点击开始翻译”')
      } catch (err) {
        if (this.isUploadCanceledError(err)) {
          this.showToast('已取消上传', 'info')
        } else {
          this.showError((err && err.message) || '字幕上传失败')
        }
      } finally {
        this.endUploadProgress()
        this.isProcessing = false
      }
    },

    // ─── 下载 ─────────────────────────────────────────────
    downloadInputRowSubtitle (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row || !row.srtUrl) {
        this.showError('字幕文件地址不存在，请刷新后重试')
        return
      }
      const base = (this.fileDisplayName(row) || 'subtitle').replace(/\.[^/.]+$/, '')
      this.triggerDownload(row.srtUrl, `${base}.srt`)
    },
    downloadInputRowTranslatedVideo (rowIndex) {
      const row = this.inputRows[rowIndex]
      if (!row || !row.translatedUrl) {
        this.showError('翻译视频尚未就绪')
        return
      }
      const base = (this.fileDisplayName(row) || 'translated_video').replace(/\.[^/.]+$/, '')
      this.triggerDownload(row.translatedUrl, `${base}_translated.mp4`)
    },
    downloadSubtitle (job) {
      if (!job.subtitleUrl) {
        this.showError('字幕文件地址不存在，请刷新后重试')
        return
      }
      const base = (job.filename || 'subtitle').replace(/\.[^/.]+$/, '')
      this.triggerDownload(job.subtitleUrl, `${base}.srt`)
    },
    downloadTranslatedVideo (job) {
      if (!job.translatedUrl) {
        this.showError('翻译视频尚未就绪')
        return
      }
      const base = (job.filename || 'translated_video').replace(/\.[^/.]+$/, '')
      this.triggerDownload(job.translatedUrl, `${base}_translated.mp4`)
    },

    // ─── 收费确认 ──────────────────────────────────────────
    handleConfirmPricing () {
      const action = this.pendingAction
      this.showConfirmPricing = false
      this.pendingAction = null
      if (!action) return
      if (action.type === 'subtitle') {
        this.confirmGenerateSubtitle(action.rowIndex)
      } else if (action.type === 'translation') {
        if (action.job) {
          this.confirmStartTranslation({ type: 'job', job: action.job })
        } else {
          this.confirmStartTranslation({ type: 'row', rowIndex: action.rowIndex })
        }
      }
    },

    // ─── 删除逻辑 ──────────────────────────────────────────
    deleteStepwiseJob (job) {
      this.deleteMode = 'stepwiseJob'
      this.pendingDeleteJob = job
      this.showDeleteConfirm = true
    },
    async handleDeleteConfirm () {
      this.showDeleteConfirm = false
      if (this.deleteMode === 'inputRow' && this.pendingRowIndex !== null) {
        this.inputRows.splice(this.pendingRowIndex, 1)
        this.pendingRowIndex = null
        this.showSuccess('任务行已删除')
      } else if (this.deleteMode === 'stepwiseJob') {
        await this.confirmDeleteStepwiseJob()
      }
      this.deleteMode = null
      this.pendingRowIndex = null
    },
    async confirmDeleteStepwiseJob () {
      const job = this.pendingDeleteJob
      if (!job) return
      const deletingCurrent = this.currentRow &&
        (this.currentRow.srtTaskId === job.srtTaskId || this.currentRow.translateTaskId === job.translateTaskId)
      try {
        const relatedRecords = this.stepwiseRecords.filter(r =>
          r.task_id === job.translateTaskId ||
          r.task_id === job.srtTaskId
        )
        for (const record of relatedRecords) {
          await deleteUserRecord({
            operation_type: 'stepwiseVideoTranslation',
            phoneNum: record.user_phone,
            taskId: record.task_id
          })
        }
        this.showSuccess('删除任务成功')
        await this.refreshStepwiseStatus()
        if (deletingCurrent) this.startNewTask()
      } catch (error) {
        this.showError('删除记录失败')
      } finally {
        this.pendingDeleteJob = null
      }
    },

    // ─── 状态刷新 ──────────────────────────────────────────
    async refreshStepwiseStatus (silent = false) {
      if (!this.isLoggedIn) {
        if (!silent) this.showError('请先登录')
        return
      }
      this.isRefreshing = true
      try {
        const response = await getUserRecords({ table_names: ['stepwiseVideoTranslation'] })
        const records = (response && response.result) || []
        this.stepwiseRecords = records
        this.syncTranslationJobsWithRecords()
        this.syncInputRowsWithJobs()
        if (!silent) this.showSuccess('状态已更新')
      } catch (error) {
        if (!silent) this.showError('刷新状态失败')
      } finally {
        this.isRefreshing = false
      }
    },
    syncTranslationJobsWithRecords () {
      const existingJobsMap = new Map(this.translationJobs.map(j => [j.srtTaskId || j.id, j]))

      this.translationJobs = this.stepwiseRecords.map(record => {
        const taskId = record.task_id
        const isTranslation = record.operation_type === 'translateWithEditedSubtitle'
        const status = record.task_status
        const existing = existingJobsMap.get(taskId)

        const job = {
          id: taskId,
          srtTaskId: taskId,
          translateTaskId: isTranslation ? taskId : '',
          operationType: record.operation_type,
          filename: record.input_filename || 'unknown',
          videoFileUrl: record.input_file_url || '',
          createdAt: record.upload_time || '',
          duration: record.video_duration || 0,
          targetLanguage: record.target_lang || 'en',
          hardSubtitle: !!record.hardSubtitle,
          subtitleOption: record.subtitleOption || 'target',
          hardSubtitleType: record.hardSubtitleType || 'target',
          lipSync: !!record.lipSync,
          subtitleStatus: 'idle',
          subtitleUrl: record.generated_srt_url || '',
          uploadedSubtitleUrl: record.subtitle_file_url || '',
          uploadedSubtitleName: record.subtitle_file_url ? '已提交' : '',
          translationStatus: 'idle',
          translatedUrl: ''
        }

        if (isTranslation) {
          job.subtitleStatus = 'completed'
          job.translationStatus = status === 'completed'
            ? 'completed'
            : (status === 'failed' ? 'failed' : 'translating')
          job.translatedUrl = status === 'completed' ? (record.result_file_url || '') : ''
        } else {
          job.subtitleStatus = status === 'completed'
            ? 'completed'
            : (status === 'failed' ? 'failed' : 'processing')
          if (status === 'completed') job.subtitleUrl = record.result_file_url || job.subtitleUrl
        }

        // 保留本地刚上传、后端尚未返回的字幕信息
        if (existing && existing.uploadedSubtitleUrl && !job.uploadedSubtitleUrl) {
          job.uploadedSubtitleUrl = existing.uploadedSubtitleUrl
          job.uploadedSubtitleName = existing.uploadedSubtitleName
          if (job.translationStatus === 'idle') job.translationStatus = existing.translationStatus
        }
        return job
      })
    },
    // 把任务行与后端记录同步（任务行仅跟踪未在历史中显示的任务）
    syncInputRowsWithJobs () {
      this.inputRows.forEach(row => {
        if (!row.srtTaskId) return
        const job = this.translationJobs.find(j => j.srtTaskId === row.srtTaskId)
        if (!job) return
        row.srtStatus = job.subtitleStatus === 'completed' ? 'completed' : (job.subtitleStatus === 'failed' ? 'failed' : job.subtitleStatus)
        if (job.subtitleUrl) row.srtUrl = job.subtitleUrl
        if (!row.uploadedSubtitleUrl && job.uploadedSubtitleUrl) {
          row.uploadedSubtitleUrl = job.uploadedSubtitleUrl
          row.uploadedSubtitleName = job.uploadedSubtitleName
        }
        if (job.translationStatus === 'completed') {
          row.translationStatus = 'completed'
          row.translatedUrl = job.translatedUrl
          row.translateTaskId = job.translateTaskId || row.translateTaskId
        } else if (job.translationStatus === 'failed') {
          row.translationStatus = 'failed'
        } else if (job.translationStatus === 'translating') {
          row.translationStatus = 'translating'
          row.translateTaskId = job.translateTaskId || row.translateTaskId
        } else if (row.translationStatus !== 'ready' && row.uploadedSubtitleUrl) {
          row.translationStatus = 'ready'
        }
      })
    }
  }
}
</script>

<style scoped>
/* ========== 全局容器 ========== */
.video-translation-view {
  width: 780px;
  min-height: 100vh;
  background-color: #1a1a1a;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ========== Toast ========== */
.toast-container {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 300px;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.toast-container.success { background-color: #4caf50; color: white; }
.toast-container.error   { background-color: #f44336; color: white; }
.toast-container.warning { background-color: #ff9800; color: white; }
.toast-container.info    { background-color: #2196f3; color: white; }

.toast-content { display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 500; }
.toast-icon    { font-size: 20px; font-weight: bold; }
.toast-message { flex: 1; }

/* ========== 主容器 ========== */
.video-translation {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  max-width: 1200px;
  width: 780px;
  min-width: 780px;
  margin: 0 auto;
  padding: 10px;
  color: white;
  background-color: #1a1a1a;
  height: auto;
}

h2 { text-align: center; margin: 10px 0 5px; font-size: 20px; font-weight: 600; }

.notice-text {
  font-size: 12px;
  color: #ffb74d;
  text-align: center;
  margin: 0 0 10px 0;
  line-height: 1.5;
  padding: 0 15px;
}

.subtitle-desc { margin-bottom: 15px; color: #bbb; font-size: 15px; text-align: center; }

/* ========== 工作流程说明 ========== */
.workflow-info {
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 6px;
  margin-bottom: 10px;
}

.workflow-steps { display: flex; align-items: center; justify-content: space-between; }
.workflow-step { flex: 1; display: flex; gap: 12px; align-items: center; opacity: 0.6; }
.workflow-step.active,
.workflow-step.completed { opacity: 1; }

.step-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.workflow-step.active .step-number { background-color: #00bcd4; }

.step-content { flex: 1; }
.step-title { font-size: 13px; font-weight: 500; margin-bottom: 4px; }
.step-desc  { font-size: 12px; color: #999; }
.workflow-arrow { color: #666; font-size: 20px; padding: 0 10px; flex-shrink: 0; }

/* ========== 分步翻译容器 ========== */
.stepwise-container {
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.current-task-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: #1a1a1a;
  border-bottom: 10px solid #fff;
}

/* ========== 当前任务向导 ========== */
.wizard-card {
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 0;
}

.wizard-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.wizard-title-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.wizard-title { margin: 0; font-size: 16px; font-weight: 600; }

.wizard-title-name {
  color: #bbb;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 420px;
}

.wizard-header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

.wizard-subtitle {
  margin: 4px 0 0;
  color: #999;
  font-size: 12px;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wizard-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.wizard-step {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: #222;
  opacity: 0.55;
  cursor: pointer;
  transition: all 0.2s;
}

.wizard-step.active,
.wizard-step.completed { opacity: 1; }

.wizard-step.active {
  background-color: rgba(0, 188, 212, 0.12);
  box-shadow: inset 0 0 0 1px rgba(0, 188, 212, 0.4);
}

.wizard-step.completed .step-number { background-color: #4caf50; }
.wizard-step.active .step-number { background-color: #00bcd4; }
.wizard-step.disabled { cursor: not-allowed; opacity: 0.3; }
.wizard-step .step-number { width: 22px; height: 22px; font-size: 12px; }
.wizard-step .step-title { font-size: 12px; }
.wizard-step .step-desc { font-size: 10px; color: #999; }

.wizard-body { margin-top: 0; }

.wizard-panel {
  background-color: #222;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  padding: 14px;
}

.wizard-panel h4 { margin: 0 0 12px; font-size: 14px; }
.wizard-panel h4 { text-align: center; }

.wizard-current-file {
  text-align: center;
  color: #bbb;
  font-size: 12px;
  margin-bottom: 12px;
}

.wizard-upload-area {
  height: 250px;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #555;
  border-radius: 10px;
  cursor: pointer;
  padding: 16px;
  box-sizing: border-box;
  transition: all 0.2s;
}

.wizard-upload-area:hover,
.wizard-upload-area.dragging {
  border-color: #00bcd4;
  background-color: rgba(0, 188, 212, 0.08);
}

.wizard-upload-area p { font-size: 12px; margin: 4px 0; color: #ccc; }
.wizard-upload-area small { font-size: 11px; color: #999; }

.wizard-upload-area--preview {
  position: relative;
  padding: 0;
  overflow: hidden;
  cursor: default;
}

.wizard-upload-area--preview .video-preview {
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  background-color: #000;
}

.wizard-upload-area--preview .preview-placeholder {
  color: #999;
  font-size: 12px;
}

.video-delete-btn {
  top: 8px;
  right: 8px;
  z-index: 2;
}

.wizard-file-info {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background-color: #2f2f2f;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.file-label { color: #999; font-size: 11px; margin-bottom: 4px; }
.file-name { color: #fff; font-size: 13px; word-break: break-all; }
.file-meta { color: #999; font-size: 11px; margin-top: 4px; }

.wizard-form-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  margin: 12px 0;
  font-size: 12px;
}

.wizard-form-row .lang-select { margin-left: 50px; }
.wizard-form-row .generate-subtitle-btn,
.wizard-form-row .start-translate-btn { margin-left: auto; }

.wizard-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  justify-content: center;
}

.wizard-lock {
  background-color: #2f2f2f;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 12px;
  color: #aaa;
  font-size: 12px;
  line-height: 1.6;
}

.link-btn {
  background: none;
  border: none;
  color: #00bcd4;
  cursor: pointer;
  padding: 0 4px;
  font-size: 12px;
}

.option-row { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 12px; }
.option-row--center { justify-content: center; }
.option-checkbox { display: flex; align-items: center; gap: 6px; font-size: 12px; cursor: pointer; }
.subtitle-type-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  margin-bottom: 10px;
}
.cost-line { color: #ccc; font-size: 12px; margin: 10px 0; }
.wizard-panel--center .cost-line,
.wizard-panel--center .status-line,
.wizard-panel--center .wizard-lock { text-align: center; }
.wizard-panel--center .wizard-file-info { justify-content: center; text-align: center; }
.cost-detail { color: #888; font-size: 11px; }
.status-line { min-height: 22px; font-size: 12px; margin: 10px 0; }
.status-success { color: #4caf50; }
.wizard-lock--center { text-align: center; }
.wizard-empty { color: #999; padding: 24px; text-align: center; }

.history-section {
  margin-top: 0;
  background-color: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  padding: 14px;
}
.history-section .job-table-header { justify-content: space-between; }
.history-list { display: flex; flex-direction: column; gap: 10px; }

.history-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  background-color: #222;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  padding: 12px;
}

.history-main { min-width: 0; flex: 1; }
.history-name { font-size: 13px; color: #fff; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-meta { font-size: 11px; color: #999; margin-top: 5px; }
.history-status { font-size: 11px; margin-top: 6px; }
.history-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }

.job-tracking-section { width: 100%; margin-top: 5px; }

.job-table-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
  min-height: 30px;
}

.job-table-title { margin: 0; font-size: 16px; font-weight: 600; }

.header-buttons {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
}

.add-job-btn {
  padding: 3px 12px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.add-job-btn:hover { background-color: #388e3c; }

.btn-icon { font-size: 14px; font-weight: bold; }

.refresh-status-btn {
  padding: 3px 12px;
  background-color: #00bcd4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.refresh-status-btn:hover:not(:disabled) { background-color: #0097a7; }
.refresh-status-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.job-table-wrapper {
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.job-table-container {
  height: 100%;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #444;
  border-radius: 8px;
  width: 100%;
}

.job-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  color: white;
}

.job-table thead th {
  position: sticky;
  top: 0;
  background-color: #333;
  color: white;
  font-weight: bold;
  padding: 6px 4px;
  text-align: center;
  border: 1px solid #444;
  z-index: 10;
  font-size: 11px;
}

.job-table tbody td {
  padding: 8px 6px;
  text-align: center;
  border: 1px solid #444;
  background-color: #2a2a2a;
  font-size: 11px;
  vertical-align: middle;
}

.job-table tbody tr:nth-child(even) td { background-color: #333; }

/* ─── 新建任务输入行高亮 ─── */
.input-row td {
  background-color: rgba(0, 188, 212, 0.07) !important;
  border-color: rgba(0, 188, 212, 0.35) !important;
}

.input-row.row-highlight td {
  background-color: rgba(0, 188, 212, 0.12) !important;
  border-color: rgba(0, 188, 212, 0.5) !important;
}

/* ─── 表格内上传区域 ─── */
.table-upload-area {
  min-height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #555;
  border-radius: 8px;
  cursor: pointer;
  padding: 6px 4px;
  transition: all 0.3s;
  box-sizing: border-box;
}

.table-upload-area:hover,
.table-upload-area.dragging {
  background-color: rgba(0, 188, 212, 0.1);
  border-color: #00bcd4;
}

.table-upload-area:focus-visible {
  outline: 2px solid #00bcd4;
  outline-offset: 2px;
}

.table-upload-icon { font-size: 22px; color: #00bcd4; line-height: 1; }
.table-upload-area p     { font-size: 10px; margin: 3px 0 2px; color: #ccc; }
.table-upload-area small { font-size: 9px; color: #999; }

/* ─── 文件名显示 ─── */
.table-file-cell {
  position: relative;
  padding: 4px 20px 4px 4px;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.cell-delete-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(244, 67, 54, 0.85);
  border: none;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: background-color 0.2s;
}

.cell-delete-btn:hover:not(:disabled) { background-color: #f44336; }
.cell-delete-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.table-filename {
  display: block;
  font-size: 10px;
  color: #e0e0e0;
  word-break: break-all;
  line-height: 1.3;
}

/* ─── 已上传字幕显示 ─── */
.uploaded-subtitle-info {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}

.uploaded-check { color: #4caf50; font-size: 12px; font-weight: bold; flex-shrink: 0; }

.uploaded-subtitle-name {
  font-size: 10px;
  color: #4caf50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80px;
}

/* ─── 空历史记录提示 ─── */
.empty-history-msg {
  text-align: center;
  color: #666;
  padding: 10px !important;
  font-size: 11px;
  font-style: italic;
}

.filename-cell {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ─── 状态文字 ─── */
.status-processing { color: #ffa726; font-size: 11px; }
.status-failed     { color: #f44336; font-size: 11px; }
.status-idle       { color: #666; font-size: 11px; }

/* ─── 按钮通用 ─── */
.download-btn,
.upload-btn,
.delete-job-btn,
.generate-subtitle-btn,
.start-translate-btn,
.reset-job-btn,
.delete-row-btn {
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  transition: all 0.2s;
  white-space: nowrap;
  margin: 2px;
}

.download-btn { background-color: #00bcd4; }
.download-btn:hover { background-color: #0097a7; }

.upload-btn { background-color: #4caf50; }
.upload-btn:hover:not(:disabled) { background-color: #388e3c; }
.upload-btn:disabled { background-color: #555; cursor: not-allowed; opacity: 0.7; }

.delete-job-btn { background-color: #f44336; }
.delete-job-btn:hover { background-color: #d32f2f; }

.delete-row-btn { background-color: #795548; }
.delete-row-btn:hover { background-color: #5d4037; }

.generate-subtitle-btn {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  padding: 5px 8px;
}

.generate-subtitle-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #f57c00, #e65100);
  transform: translateY(-1px);
}

.generate-subtitle-btn:disabled { background: #555; cursor: not-allowed; opacity: 0.7; }

.start-translate-btn {
  background: linear-gradient(135deg, #00bcd4, #0097a7);
  padding: 5px 8px;
}

.start-translate-btn:hover {
  background: linear-gradient(135deg, #0097a7, #00838f);
  transform: translateY(-1px);
}

.reset-job-btn { background-color: #607d8b; }
.reset-job-btn:hover { background-color: #455a64; }

.download-btn:focus-visible,
.upload-btn:focus-visible,
.delete-job-btn:focus-visible,
.generate-subtitle-btn:focus-visible,
.start-translate-btn:focus-visible,
.reset-job-btn:focus-visible,
.delete-row-btn:focus-visible,
.add-job-btn:focus-visible,
.refresh-status-btn:focus-visible,
.cancel-btn:focus-visible,
.confirm-btn:focus-visible,
.cell-delete-btn:focus-visible,
.lang-select:focus-visible,
.noise-checkbox input:focus-visible {
  outline: 2px solid #00bcd4;
  outline-offset: 2px;
}

/* ─── 翻译设置 ─── */
.translation-settings {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  width: 100%;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 6px;
  font-size: 11px;
  color: white;
}

.lang-select {
  padding: 3px 6px;
  background-color: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
}

.lang-select:hover:not(:disabled) { border-color: #00bcd4; }
.lang-select:disabled { cursor: not-allowed; opacity: 0.6; }

.noise-checkbox {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  cursor: pointer;
}

.noise-checkbox input[type="checkbox"] { cursor: pointer; }
.noise-checkbox input[type="checkbox"]:disabled { cursor: not-allowed; }

.terms {
  font-size: 12px;
  color: #888;
  margin-top: 15px;
  text-align: center;
  line-height: 1.5;
}

/* ========== 对话框 ========== */
.confirm-dialog-overlay,
.processing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-dialog,
.processing-dialog {
  background-color: #2a2a2a;
  border-radius: 16px;
  width: 320px;
  color: white;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
}

.confirm-dialog { padding: 16px; }
.confirm-dialog h3 { font-size: 15px; margin: 0 0 10px; font-weight: 550; }
.confirm-dialog p  { margin: 0 0 12px; color: #ccc; line-height: 1.5; font-size: 13px; }
.confirm-dialog-buttons { display: flex; justify-content: flex-end; gap: 12px; }

.cancel-btn {
  background-color: transparent;
  border: 1px solid #666;
  color: #ccc;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: #999;
  color: white;
}

.confirm-btn {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.confirm-btn:hover { background-color: #d32f2f; transform: translateY(-2px); }

.confirm-btn--primary { background: linear-gradient(135deg, #00bcd4, #0097a7); }
.confirm-btn--primary:hover { background: linear-gradient(135deg, #0097a7, #00838f); }

.processing-dialog { padding: 40px; text-align: center; }

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #444;
  border-top-color: #00bcd4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-dialog h3 { margin: 0 0 12px; font-size: 18px; font-weight: 600; }
.processing-dialog p  { margin: 0; color: #999; font-size: 14px; }

/* 上传进度条 + 取消按钮 */
.upload-progress { margin-top: 4px; }

.upload-progress-track {
  width: 100%; height: 8px; border-radius: 4px;
  background-color: #444; overflow: hidden;
}

.upload-progress-bar {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #00bcd4, #0097a7);
  transition: width 0.2s ease-out;
}

.upload-progress-text { margin: 8px 0 0; color: #ccc; font-size: 12px; }
.upload-slow-tip      { margin: 6px 0 0; color: #ffb74d; font-size: 11px; line-height: 1.4; }

.cancel-upload-btn {
  margin-top: 14px; padding: 6px 16px;
  background-color: transparent; border: 1px solid #666; color: #ccc;
  border-radius: 8px; cursor: pointer; font-size: 12px; transition: all 0.2s;
}

.cancel-upload-btn:hover { background-color: rgba(255, 255, 255, 0.1); border-color: #999; color: white; }

/* ========== 移动端 ========== */
@media screen and (max-width: 768px) {
  .toast-container { min-width: 250px; padding: 12px 16px; top: 60px; }
  .toast-content   { font-size: 12px; }
  .toast-icon      { font-size: 16px; }
  .wizard-steps    { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .history-item    { flex-direction: column; }
  .history-actions { justify-content: flex-start; }

  .video-translation::before {
    content: "可左右滑动查看完整内容";
    display: block;
    text-align: center;
    color: #00bcd4;
    font-size: 12px;
    padding: 8px;
    background-color: rgba(0, 188, 212, 0.1);
    border-radius: 4px;
    margin-bottom: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-container,
  .spinner {
    animation: none;
  }

  .table-upload-area,
  .generate-subtitle-btn,
  .start-translate-btn,
  .confirm-btn,
  .cancel-btn {
    transition: none;
  }
}
</style>
